/**
 * Cuenta de Resultados (P&L).
 *
 * Selector trimestre/año + filtro por línea de servicio. Tabla jerárquica con
 * los bloques del PGC (ingresos explotación, aprovisionamientos, personal,
 * otros gastos, financieros, impuestos) y comparativa con el año anterior.
 *
 * La API deja leerla a ADMIN y CONTABILIDAD (la gestoría).
 * Debajo de los filtros, los ratios de la cuenta de resultados y la cascada del mismo periodo.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, BookOpen, ChevronDown, ChevronRight, FileText, Loader2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import { useServiceLines } from '../lib/service-lines'
import PnlRatiosAndWaterfall, {
  type PnlKpiPeriod,
} from '../components/accounting/PnlRatiosAndWaterfall'
import { YearComparison } from '../components/accounting/kpis/YearComparison'
import ReportPdfMenu, { type ReportPdfPeriod } from '../components/accounting/ReportPdfMenu'
import type { PnlReport, PnlSection } from '@jr2os/shared'

const CURRENT_YEAR = new Date().getFullYear()

type QuarterMode = 'T1' | 'T2' | 'T3' | 'T4' | 'YEAR' | 'MONTH'

/** Las dos pestañas: el periodo elegido, o la evolución de los últimos ejercicios. */
type PnlTab = 'SUMMARY' | 'EVOLUTION'

interface QuarterDef {
  value: QuarterMode
  /** i18n key relative al ns 'accounting'. */
  labelKey: string
  /** Mes 0-indexed. */
  fromMonth: number
  toMonth: number
}

const QUARTERS: QuarterDef[] = [
  { value: 'YEAR', labelKey: 'quarters.year', fromMonth: 0, toMonth: 11 },
  { value: 'T1', labelKey: 'quarters.t1', fromMonth: 0, toMonth: 2 },
  { value: 'T2', labelKey: 'quarters.t2', fromMonth: 3, toMonth: 5 },
  { value: 'T3', labelKey: 'quarters.t3', fromMonth: 6, toMonth: 8 },
  { value: 'T4', labelKey: 'quarters.t4', fromMonth: 9, toMonth: 11 },
  // El rango del mes concreto lo calcula `from/to` con el mes elegido.
  { value: 'MONTH', labelKey: 'quarters.month', fromMonth: 0, toMonth: 11 },
]

const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const

/** Última fecha del mes (1-indexed mes 1-12). */
function lastDayOfMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate()
}

/** Formato YYYY-MM-DD. */
function fmt(year: number, month0: number, day: number): string {
  return `${year}-${String(month0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Bloques cuyo subtotal queremos resaltar (totales/resultados intermedios). */
const HIGHLIGHTED_KEYS: ReadonlySet<PnlSection['key']> = new Set([
  'RESULTADO_EXPLOTACION',
  'RESULTADO_FINANCIERO',
  'RESULTADO_ANTES_IMPUESTOS',
  'RESULTADO_EJERCICIO',
])

export default function AccountingPnl() {
  const { t } = useTranslation('accounting')
  const { lines } = useServiceLines()
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [quarter, setQuarter] = useState<QuarterMode>('YEAR')
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1)
  const [serviceLine, setServiceLine] = useState<string>('')
  const [tab, setTab] = useState<PnlTab>('SUMMARY')

  const [report, setReport] = useState<PnlReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y--) arr.push(y)
    return arr
  }, [])

  const { from, to } = useMemo(() => {
    const def = QUARTERS.find((q) => q.value === quarter) ?? QUARTERS[0]!
    // Un mes concreto: del 1 al último día de ESE mes.
    const m0 = quarter === 'MONTH' ? month - 1 : null
    const fromStr = fmt(year, m0 ?? def.fromMonth, 1)
    const toStr = fmt(year, m0 ?? def.toMonth, lastDayOfMonth(year, (m0 ?? def.toMonth) + 1))
    return { from: fromStr, to: toStr }
  }, [year, quarter, month])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ from, to })
      if (serviceLine) params.set('serviceLine', serviceLine)
      const data = await api.get<PnlReport>(`/accounting/reports/pnl?${params.toString()}`)
      setReport(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.loadFailed')
      setError(text)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [from, to, serviceLine, t])

  useEffect(() => {
    void load()
  }, [load])

  // El informe financiero usa la nomenclatura de la API (Q1…Q4), la pantalla la suya (T1…T4).
  const reportPeriod: ReportPdfPeriod =
    quarter === 'YEAR' || quarter === 'MONTH'
      ? quarter
      : (`Q${quarter.slice(1)}` as ReportPdfPeriod)

  // El periodo elegido empieza después de hoy (un mes futuro, el año que viene): no hay informe que hacer.
  const periodNotStarted = useMemo(() => {
    const t = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return from > `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`
  }, [from])

  const downloadPdf = async () => {
    const params = new URLSearchParams({ from, to })
    if (serviceLine) params.set('serviceLine', serviceLine)
    try {
      await downloadFile(
        `/accounting/reports/pnl/pdf?${params}`,
        `perdidas-y-ganancias-${from}-${to}.pdf`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <div className="p-2 rounded-lg bg-jr2-500/15 text-jr2-700 dark:text-jr2-300">
          <BookOpen size={20} />
        </div>
        <div className="flex-1">
          {/* Clave propia: `title` del espacio «accounting» lo comparten los
              KPIs, los Modelos AEAT y el Cierre, y tocarlo los renombraría a
              los cuatro. */}
          <h1 className="text-xl font-semibold">{t('pnlTitle')}</h1>
          <p className="text-sm t-muted">{t('subtitle')}</p>
        </div>
        <ReportPdfMenu
          year={year}
          period={reportPeriod}
          month={month}
          notStarted={periodNotStarted}
          onError={setError}
        />
        <button
          type="button"
          onClick={() => void downloadPdf()}
          className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5"
        >
          <FileText size={14} /> {t('pnlPdf')}
        </button>
      </header>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Pestañas: el periodo elegido, o cómo evolucionan los últimos ejercicios (la «Comparativa
          anual» de la antigua pantalla de KPIs). */}
      <div className="flex gap-6 border-b border-[var(--border)]">
        {(['SUMMARY', 'EVOLUTION'] as const).map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={[
              'py-2.5 text-sm border-b-2 -mb-px transition-colors',
              tb === tab
                ? 'border-jr2-500 t-primary font-semibold'
                : 'border-transparent t-muted hover:t-primary',
            ].join(' ')}
          >
            {t(tb === 'SUMMARY' ? 'pnlTabs.summary' : 'pnlTabs.evolution')}
          </button>
        ))}
      </div>

      {tab === 'EVOLUTION' && <YearComparison years={yearOptions} />}

      {tab === 'SUMMARY' && (
        <>
          {/* Selectores */}
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
            <div>
              <label className="block text-xs font-medium t-muted mb-1">{t('filters.year')}</label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium t-muted mb-1">
                {t('filters.period')}
              </label>
              <select
                value={quarter}
                onChange={(e) => setQuarter(e.target.value as QuarterMode)}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
              >
                {QUARTERS.map((q) => (
                  <option key={q.value} value={q.value}>
                    {t(q.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            {quarter === 'MONTH' && (
              <div>
                <label className="block text-xs font-medium t-muted mb-1">
                  {t('filters.month')}
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
                >
                  {MONTH_KEYS.map((k, i) => (
                    <option key={k} value={i + 1}>
                      {t(`months.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium t-muted mb-1">
                {t('filters.serviceLine')}
              </label>
              <select
                value={serviceLine}
                onChange={(e) => setServiceLine(e.target.value)}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
              >
                <option value="">{t('serviceLines.all')}</option>
                {lines.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-xs t-muted ml-auto">{t('rangeLabel', { from, to })}</div>
          </div>

          {/* Los ratios y la cascada del MISMO periodo, justo debajo de los filtros. */}
          <PnlRatiosAndWaterfall
            year={year}
            period={
              (quarter === 'YEAR' || quarter === 'MONTH'
                ? quarter
                : quarter.replace('T', 'Q')) as PnlKpiPeriod
            }
            month={month}
            serviceLine={serviceLine}
          />

          {loading && (
            <div className="flex items-center gap-2 text-sm t-muted">
              <Loader2 size={16} className="animate-spin" />
              {t('loading')}
            </div>
          )}

          {report && !loading && (
            <div className="overflow-hidden rounded-xl border border-[var(--border)]">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-input-soft)] t-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">{t('table.accountBlock')}</th>
                    <th className="text-right px-4 py-2 font-medium">{t('table.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.sections.map((sec) => {
                    const isHighlighted = HIGHLIGHTED_KEYS.has(sec.key)
                    return (
                      <SectionRows
                        key={sec.key}
                        section={sec}
                        highlighted={isHighlighted}
                        year={year}
                      />
                    )
                  })}
                </tbody>
              </table>

              {/* Comparativa */}
              <div className="bg-[var(--bg-input-soft)] border-t border-[var(--border)] px-4 py-3 flex justify-between text-sm">
                <span className="t-muted">{t('previousYearResult')}</span>
                <span className="font-mono">
                  {report.comparisons.previousYear
                    ? formatCents(report.comparisons.previousYear.finalResult)
                    : '—'}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SectionRows({
  section,
  highlighted,
  year,
}: {
  section: PnlSection
  highlighted: boolean
  /** Ejercicio del informe: viaja al Mayor en el enlace de cada cuenta. */
  year: number
}) {
  const { t } = useTranslation(['accounting'])
  // Plegado por defecto, como en Ledgeria: lo primero que se lee de una cuenta
  // de resultados son los subtotales; el desglose se abre cuando hace falta.
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr
        className={
          highlighted
            ? 'bg-[var(--bg-hover)] border-t border-[var(--border)] t-primary'
            : 'bg-[var(--bg-input-soft)] border-t border-[var(--border)]'
        }
      >
        <td className={highlighted ? 'px-4 py-2 font-semibold' : 'px-4 py-2 font-medium'}>
          {section.accounts.length === 0 ? (
            section.title
          ) : (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 text-left hover:underline"
              aria-expanded={open}
            >
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {section.title}
              <span className="t-faint text-[11px] font-normal">({section.accounts.length})</span>
            </button>
          )}
        </td>
        <td
          className={
            highlighted
              ? 'px-4 py-2 text-right font-mono font-semibold'
              : 'px-4 py-2 text-right font-mono'
          }
        >
          {formatCents(section.subtotal)}
        </td>
      </tr>
      {open &&
        section.accounts.map((a) => (
          <tr key={`${section.key}-${a.code}`} className="border-t border-[var(--border)]/60">
            <td className="px-4 py-1.5 pl-10 t-secondary">
              {/* De la cifra al porqué: el Mayor de esa cuenta, en el mismo
                  ejercicio. Sin esto, para saber qué hay dentro de «Otros
                  gastos» había que copiar el código a mano en otra pantalla. */}
              <Link
                to={`/accounting/ledger?accountCode=${encodeURIComponent(a.code)}&year=${year}`}
                className="hover:underline"
                title={t('openLedger')}
              >
                <span className="t-muted mr-2 font-mono">{a.code}</span>
                {a.name}
              </Link>
            </td>
            <td className="px-4 py-1.5 text-right font-mono t-secondary">
              {formatCents(a.amount)}
            </td>
          </tr>
        ))}
    </>
  )
}
